import { appFormsTreeAreaNodesExtended } from 'app/services/autoLockForms/autoLockForms';
import { AutoLockFormsLevelType } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

/**
 * Glue between a loan-editor nav item and the `selectIncompleteLinkedNav`
 * roll-up map (DEV-4496). A nav item knows its own identity (the outer rail
 * knows a `{ name, levelType }` from `tabNameToAutoLockArea`; an inner
 * EditModal section knows its kebab href like `loan-info`), NOT its appFormsTree
 * area id — so these helpers resolve the item to the area id(s) the roll-up map
 * is keyed by, reusing the SAME `appFormsTreeAreaNodesExtended` lookups the
 * autolock feature already uses (`checkIsAreaPageLocked` / `checkIsTabHidden`).
 */

// Outer rail: a `{ name, levelType }` (e.g. propertyAndLoan / SECTION) → its area
// node id(s). Mirrors `checkIsAreaPageLocked`'s name+levelType match.
export const getAreaIdsByNameAndLevel = (areaName, levelType) => {
  if (!areaName || !levelType) {
    return [];
  }

  return appFormsTreeAreaNodesExtended
    .filter(node => node.levelType === levelType && node.name === areaName)
    .map(node => node.id);
};

// Inner section nav: a section href (`loan-info`) → the TAB area node id(s) whose
// camelCased tab name matches. Mirrors `checkIsTabHidden`'s `_.camelCase` match.
export const getTabAreaIdsBySectionHref = sectionHref => {
  if (!sectionHref) {
    return [];
  }

  const camelTabName = _.camelCase(sectionHref);

  return appFormsTreeAreaNodesExtended
    .filter(node => node.levelType === AutoLockFormsLevelType.TAB && node.tab === camelTabName)
    .map(node => node.id);
};

// Combines the loan-scoped roll-up with one borrower's roll-up into the map that borrower's nav
// items resolve against. Counts sum per area id because a loan-level ref and a borrower-scoped one
// are independent still-needed items, each worth its own tally.
export const mergeNavNeededMaps = (loanScopedNavMap = {}, borrowerNavMap = {}) =>
  Object.entries(borrowerNavMap).reduce(
    (acc, [areaId, entry]) => ({
      ...acc,
      [areaId]: { needed: true, count: (acc[areaId]?.count ?? 0) + entry.count },
    }),
    { ...loanScopedNavMap },
  );

// Folds a nav item's resolved area ids against the roll-up map into a single
// `{ isNeeded, count }` verdict (count = needed descendant refs across the ids).
export const getNavNeededState = (navMap = {}, areaIds = []) =>
  areaIds.reduce(
    (acc, areaId) => {
      const entry = navMap[areaId];

      if (!entry?.needed) {
        return acc;
      }

      return { isNeeded: true, count: acc.count + (entry.count ?? 0) };
    },
    { isNeeded: false, count: 0 },
  );
